import { type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import NeedHelp from '@/components/NeedHelp';

type PageShellSingleProps = {
  variant?: 'single';
  children: ReactNode;
  className?: string;
};

type PageShellSplitProps = {
  variant: 'split';
  left: ReactNode;
  right: ReactNode;
  className?: string;
  leftClassName?: string;
  rightClassName?: string;
};

type PageShellProps = (PageShellSingleProps | PageShellSplitProps) & {
  /**
   * Hides the Need help pill (GIFTWEB-32 AC 2). Opt-out rather than opt-in:
   * support should be reachable everywhere unless there is a reason not to,
   * and the only reason is being mid-transfer to the bank.
   */
  showHelp?: boolean;
};

const ambientBg =
  'lg:bg-[radial-gradient(120%_90%_at_50%_-10%,#EEF1EC_0%,#E3E7E0_100%)]';

const cardShadow =
  'lg:shadow-[0_50px_100px_-40px_rgba(20,40,32,0.6),0_0_0_1px_rgba(20,40,32,0.06)]';

type LeftAlign = 'center' | 'top';

function PageShell(props: PageShellProps & { leftAlign?: LeftAlign }) {
  const showHelp = props.showHelp !== false;
  if (props.variant === 'split') {
    const leftAlign = (props as PageShellSplitProps & { leftAlign?: LeftAlign })
      .leftAlign;
    return (
      <div
        data-shell='split'
        className={cn(
          'flex min-h-screen flex-col',
          'lg:h-screen lg:min-h-0 lg:justify-center lg:px-6',
          ambientBg,
          props.className,
        )}
      >
        <div
          data-card='split'
          className={cn(
            'relative flex flex-1 flex-col',
            'lg:mx-auto lg:grid lg:h-full lg:w-full lg:max-w-[min(1180px,90vw)] lg:flex-none lg:grid-cols-[0.82fr_1.18fr] lg:overflow-hidden lg:bg-background',
            cardShadow,
          )}
        >
          {showHelp && <NeedHelp />}
          <section
            data-pane='left'
            className={cn(
              'flex flex-col lg:min-h-0 lg:overflow-y-auto lg:bg-[#F1EFE9]',
              props.leftClassName,
            )}
          >
            <div
              className={cn(
                'flex flex-col gap-6 px-5.5 pt-20 pb-4',
                'lg:w-full lg:px-10 lg:pt-10 lg:pb-8 lg:text-left',
                leftAlign === 'top' ? 'lg:mt-0' : 'lg:my-auto',
              )}
            >
              {props.left}
            </div>
          </section>
          <section
            data-pane='right'
            className={cn(
              'flex flex-1 flex-col lg:min-h-0 lg:flex-none lg:overflow-y-auto lg:bg-background',
              props.rightClassName,
            )}
          >
            <div className='flex flex-1 justify-center flex-col gap-6 px-5.5 py-4 lg:mx-auto lg:w-full lg:max-w-140 lg:pt-24 lg:pb-8'>
              {props.right}
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div
      data-shell='single'
      className={cn(
        'flex min-h-screen flex-col',
        'lg:h-screen lg:min-h-0 lg:justify-center lg:px-6',
        ambientBg,
      )}
    >
      <div
        data-card='single'
        className={cn(
          'relative flex flex-1 flex-col p-4',
          'lg:mx-auto lg:h-full lg:w-full lg:max-w-150 lg:flex-none lg:overflow-y-auto lg:bg-background lg:px-14 lg:py-14',
          cardShadow,
          props.className,
        )}
      >
        {showHelp && <NeedHelp />}
        {props.children}
      </div>
    </div>
  );
}

export default PageShell;
